/*
 * File: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice\src\Services\Users\UserService.ts
 * Project: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice
 * Created Date: Wednesday June 7th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Tuesday, 14th November 2023 08:57:08
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import Axios, { AxiosResponse } from "axios";
import Config from "Config/Config";
import ServiceGeneral from "Services/ServiceGeneral";
import UserSession from "Utils/UserSession";
import AuthenticationError from "Error/AuthenticationError";
import { FromRefTime, buildAxiosHeader, buildAxiosHeaderFile, buildURL } from "Utils/Request.utils";
import generalQueryParams from "Type/params.type";
import { UpdateUserType, UserCreationIntel, UserStatus } from "Type/user.type";
import { tranformFromUserToFormData } from "Utils/user.utils";
import { OfferType } from "Type/offer.type";
import { QueryParams } from "Type/queryParams";

/**
 * Manage user operations with the backend
 */
class UserService {
  /**
   * Login a user with the given credentials
   * @param {string} username The username
   * @param {string} password The password
   * @returns Promise with info of tokens if login successful
   * @throws Throws if an error happened when reaching back
   */
  static async login(username: string, password: string) {
    const url = Config.BACKEND.BASEURL + Config.BACKEND.PATHS.LOGIN;

    try {
      // Set the tokens in cookies for future auth through middleware
      const response = await Axios.post(url, {username, password, refresh: "true"});
      if (response.status !== 200) return response;

      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);

      return data;
    }
  }

  /**
   * Get the intels about commercials
   * @returns The users informations
   */
  static async getCommercials() {
    const myUrl = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERSCOMMERCIALS}/`;
    try {
      const response = await Axios.get(myUrl, buildAxiosHeader());
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response.data;
    } catch (e) {
      if (e instanceof AuthenticationError)
        throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  /**
   * Get the informations about me
   * @returns The user informations
   */
  static async getUserByID(userID: string) {
    const myUrl = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/${userID}`;
    const response = await Axios.get(myUrl, buildAxiosHeader());
    return response.data;
  }

  static async getUsersInfo() {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERINFO}`;
    // return Axios.get(url,² buildAxiosHeader());
    const response = await Axios.get(url, buildAxiosHeader());
    return response.data;
  }

  static async getUserSeenCars(userID: string) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.SEENCAR}/${userID}`;
    const response = await Axios.get(url, buildAxiosHeader());
    return response.data;
  }

  static async getUsers(params: generalQueryParams) {
    const myUrl = buildURL(params, `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}`);

    try {
      const response = await Axios.get(myUrl, buildAxiosHeader());
      return response.data;
    } catch (e) {
      throw e;
    }
    //   if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
    //     return undefined;
    //   }
    //   return response.data;
    // } catch (e) {
    //   if (e instanceof AuthenticationError) throw e;
    //   const data = ServiceGeneral.interpretError(e);
    //   return data;
  };

  // static getUsers(params: generalQueryParams) {
  //   const myUrl = buildURL(params, `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}`);

  //   try {
  //     return Axios.get(myUrl, buildAxiosHeader());

  //   } catch (e) {
  //     if (e instanceof AuthenticationError) throw e;
  //     const data = ServiceGeneral.interpretError(e);
  //     return data;
  //   }
  // }

  static async getMyUsers(params: generalQueryParams) {
    const myUrl = buildURL(params, `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.MYUSERS}`);

    try {
      const response = await Axios.get(myUrl, buildAxiosHeader());
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response.data;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async getConnexions(params: generalQueryParams) {
    // if (params.from && ["day", "week", "month", "year"].includes(params.from))
    if (params.from)
      params.from = FromRefTime(params.from);
    const myUrl = buildURL(params, `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CONNEXIONS}`);

    const response = await Axios.get(myUrl, buildAxiosHeader());
    return response.data;
    // try {
    //   if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
    //     return undefined;
    //   }
    //   return response.data;
    // } catch (e) {
    //   if (e instanceof AuthenticationError) throw e;
    //   const data = ServiceGeneral.interpretError(e);
    //   return data;
    // }
  }

  static async getUserFavorites(userID: string) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.FAVORITE}/${userID}`;
    try {
      const myRet = await Axios.get(url, buildAxiosHeader());
      return myRet.data;
    } catch (e) {
      throw e;
      // if (e instanceof AuthenticationError) throw e;
      // const data = ServiceGeneral.interpretError(e);
      // return data;
    }
  }

  static async addUser(user: UserCreationIntel) {
    if (user.Role === "CLIENT") {
      var url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.ADD_USER}`;
      // url += "?emailed=true"
    } else {
      var url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.ADD_ADMIN}`;
    }
    try {
      // const form = tranformFromUserToFormData(user);
      const response = await Axios.post(url, user, buildAxiosHeader());
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async updateUser(userID: string, dataToUpdate: UpdateUserType) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/${userID}`;
    const options = buildAxiosHeader();
    try {
      const response = await Axios.put(url, dataToUpdate, options);
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async updateStatus(userID: string, status: UserStatus) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/${userID}`;
    const options = buildAxiosHeader();
    try {
      const response = await Axios.put(url, {Status: status}, options);
      return response.data;
    } catch (e) {
      throw e;
    }
  }

  // static async deleteUser(userId: string) {
  //   const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/${userId}`;
  //   try {
  //     const response = await Axios.delete(url, buildAxiosHeader());
  //     if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
  //       return undefined;
  //     }
  //     return response;
  //   } catch (e) {
  //     if (e instanceof AuthenticationError) throw e;
  //     const data = ServiceGeneral.interpretError(e);
  //     return data;
  //   }
  // }

  static async setNewPassword(password: string, encrypted: string) {
    const url =
      Config.BACKEND.BASEURL +
      Config.BACKEND.PATHS.USERS +
      "/new_password/" +
      encrypted;
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = {
        Authorization: tokens.token_type + " " + tokens.access_token,
      };
    }
    try {
      const response = await Axios.post(url, { password: password }, data);
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async forgottenPassword(userMail: string) {
    const url =
      Config.BACKEND.BASEURL +
      Config.BACKEND.PATHS.USERS +
      "/password/" +
      userMail;
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = buildAxiosHeader();
    }
    try {
      const response = await Axios.get(url, buildAxiosHeader());
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async getUserRoles() {
    const url = Config.BACKEND.BASEURL + Config.BACKEND.PATHS.USERS + "/roles";
    try {
      const response = await Axios.get(url, buildAxiosHeader());
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response.data;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async getUserOfferByUserID(userID: string) {
    const url =`${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/${userID}/offers`
    try {
      const response = await Axios.get(url, buildAxiosHeader());
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response.data;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return undefined;
    }
  }

  static async getUsersBySearch(input: string, queryParams?: QueryParams) {
    const url =`${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/search/${input}`
    const fullUrl = new URL(url);
    if (queryParams !== undefined)
      for (let entrie of Object.entries(queryParams)) {
        let val = "";
        if (typeof entrie[1] === "string")
          val = entrie[1];
        else
          val = entrie[1].join(",");
        fullUrl.searchParams.set(entrie[0], val);
      }
    try {
      const response = await Axios.get<any[]>(fullUrl.toString(), buildAxiosHeader());
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response.data;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return undefined;
    }
  }

  static async resendCGV(userid: string) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/${userid}/resend_cgv`
      const response = await Axios.get<OfferType[]>(url, buildAxiosHeader());
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response.data;
  }

  static async resendEmailVerification(userid: string) {
    const url =`${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/${userid}/resend_verification`;
    try {
      const response = await Axios.get(url, buildAxiosHeader());
      return response.data;
    } catch (e) {
      throw new Error("call failed");
    }
  }

  static async manageUserParametersUpdate(userID: string, toCreate: string[], toDelete: string[]) {
    const myUrl = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/${userID}/parameters`;
    var myRet: AxiosResponse;
    myRet = await Axios.put(myUrl, {toCreate, toDelete}, buildAxiosHeader());
    return myRet;
  };

  static async getMyProfile() {
    const myUrl = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.USERS}/profile`;
    var myRet: AxiosResponse;
    myRet = await Axios.get(myUrl, buildAxiosHeader());
    return myRet.data;
  };

  static getUserRole() {
    return UserSession.getRole();
  }

  static refreshTokens() {
    return UserSession.tokenExpired();
  }

  /**
   * Signs out the current logged user
   */
  static signout() {
    UserSession.signOut();
  }
}

export default UserService;
